import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import "./FormWrapper.css";
import { Link, useLocation } from "react-router-dom";
import { API_BASE, uploadAnnouncementImage } from "../../services/apiClient";
import { submitAnnouncement } from "../../services/newspaperApi";
import { checkAdminSession, isAdminLoggedIn } from "../../pages/admin/adminAuth";
import { useSiteContent } from "../../context/SiteContentContext";
import { findFormType } from "../formTypes";
import { PrintModeContext } from "./PrintField";
import { addAgenda, removeBlockAtSelection, renumberAgendas } from "./paperEdit";
import { Success } from "../../pages/newspaper/SubmitPage";
import { readBasket } from "../../services/noticeBasket";
import "../../pages/newspaper/newspaper.css";
import DateInput from "../../components/site/DateInput";

// html2canvas lays the cloned page out at this width (desktop), whatever the device
const CAPTURE_WINDOW_WIDTH = 1280;

const thaiMonths = [
  "",
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const toBuddhistYear = (year) => {
  const numericYear = Number(year);
  if (!Number.isFinite(numericYear)) return year || "";
  return String(numericYear >= 2400 ? numericYear : numericYear + 543);
};

const toThaiMonth = (month) => {
  const numericMonth = Number(month);
  return thaiMonths[numericMonth] || month || "";
};

const formatImportedTime = (value) => {
  if (value === undefined || value === null || value === "") return value || "";

  const text = String(value).trim();
  const numeric = Number(text);
  if (Number.isFinite(numeric) && numeric >= 0 && numeric < 1) {
    const totalMinutes = Math.round(numeric * 24 * 60);
    return `${Math.floor(totalMinutes / 60)}.${String(totalMinutes % 60).padStart(2, "0")}`;
  }

  if (Number.isFinite(numeric) && numeric >= 1 && numeric <= 24) {
    const [hourText, minuteText = "0"] = text.split(".");
    return `${Number(hourText)}.${minuteText.padEnd(2, "0").slice(0, 2)}`;
  }

  const colon = text.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
  if (colon) return `${Number(colon[1])}.${String(Number(colon[2])).padStart(2, "0")}`;

  const dot = text.match(/^(\d{1,2})\.(\d{1,2})$/);
  if (dot) return `${Number(dot[1])}.${String(Number(dot[2])).padStart(2, "0")}`;

  return text;
};

const addDisplayDateParts = (formData, sourceKey, prefix) => {
  const source = String(formData[sourceKey] || "").trim();
  const ymd = source.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  const dmy = source.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);

  if (ymd) {
    formData[`${prefix}Day`] = formData[`${prefix}Day`] || String(Number(ymd[3]));
    formData[`${prefix}Month`] = toThaiMonth(formData[`${prefix}Month`] || ymd[2]);
    formData[`${prefix}Year`] = toBuddhistYear(formData[`${prefix}Year`] || ymd[1]);
    return;
  }

  if (dmy) {
    formData[`${prefix}Day`] = formData[`${prefix}Day`] || String(Number(dmy[1]));
    formData[`${prefix}Month`] = toThaiMonth(formData[`${prefix}Month`] || dmy[2]);
    formData[`${prefix}Year`] = toBuddhistYear(formData[`${prefix}Year`] || dmy[3]);
    return;
  }

  formData[`${prefix}Month`] = toThaiMonth(formData[`${prefix}Month`]);
  formData[`${prefix}Year`] = toBuddhistYear(formData[`${prefix}Year`]);
};

const formatThaiDisplayDate = (value) => {
  const source = String(value || "").trim();
  const ymd = source.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  const dmy = source.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);

  if (ymd) return `${Number(ymd[3])} ${toThaiMonth(ymd[2])} ${toBuddhistYear(ymd[1])}`;
  if (dmy) return `${Number(dmy[1])} ${toThaiMonth(dmy[2])} ${toBuddhistYear(dmy[3])}`;
  return source;
};

const normalizeImportedFormData = (formData) => {
  const normalized = { ...(formData || {}) };
  addDisplayDateParts(normalized, "issueDate", "date");
  addDisplayDateParts(normalized, "meetingDate", "meeting");
  addDisplayDateParts(normalized, "bookClosingDate", "bookClosing");
  addDisplayDateParts(normalized, "paymentDate", "payment");
  normalized.meetingTime = formatImportedTime(normalized.meetingTime);
  normalized.agenda3AtDate = formatThaiDisplayDate(
    normalized.agenda3AtDate || normalized.meetingDate
  );
  // one-box dates: "15 พฤศจิกายน 2569" for Thai papers, "15 November 2026" for the English ones
  normalized.issueDateText = formatThaiDisplayDate(normalized.issueDate);
  normalized.issueDateEn = formatEnDisplayDate(normalized.issueDate);
  normalized.meetingDateEn = formatEnDisplayDate(normalized.meetingDate);
  return normalized;
};

const EN_MONTHS = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const formatEnDisplayDate = (value) => {
  const source = String(value || "").trim();
  const ymd = source.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  const dmy = source.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  const [d, m, y] = ymd ? [ymd[3], ymd[2], ymd[1]] : dmy ? [dmy[1], dmy[2], dmy[3]] : [];
  if (!d) return source;
  const year = Number(y) > 2400 ? Number(y) - 543 : Number(y); // English papers use the Christian year
  return `${Number(d)} ${EN_MONTHS[Number(m)] || m} ${year}`;
};

// Excel import → paper boxes. The papers have no field names, so values go into the inputs in page order;
// each template lists, box by box, which imported value it shows (null = leave empty). Checked 27-10-2569 against
// the text in front of every box of every template. 28-10-2569: the signature line (null — signed by hand) is
// followed by the director's name box inside "( … )", which gets signName.
const MEETING_14 = ["company", "dateDay", "dateMonth", "dateYear", "subjectMeetingNo1", "subjectMeetingNo2", "bodyMeetingNo1", "bodyMeetingNo2", "meetingDay", "meetingMonth", "meetingYear", "meetingTime", "meetingPlace", null, "signName"];
const MEETING_16 = [...MEETING_14.slice(0, 13), "agenda1No1", "agenda1No2", null, "signName"];
const IMPORT_FIELD_ORDER = {
  FinancialApproval: ["company", "dateDay", "dateMonth", "dateYear", "subjectMeetingNo1", "subjectMeetingNo2", "bodyMeetingNo1", "bodyMeetingNo2", "meetingDay", "meetingMonth", "meetingYear", "meetingTime", "meetingPlace", "meetingPlace2", "agenda1No1", "agenda1No2", "agenda3AtDate", "signName", "signPosition"],
  DirectorChange: MEETING_16,
  Notice3Form: MEETING_16,
  DividedPaymant: MEETING_14,
  Notice1Form: MEETING_14,
  Notice4Form: MEETING_14,
  RaiseFunds: MEETING_14,
  Relocation: MEETING_14,
  AmendmentofObjectives: ["company", "dateDay", "dateMonth", "dateYear", "subjectMeetingNo1", "subjectMeetingNo2", "company", "company", "bodyMeetingNo1", "bodyMeetingNo2", "meetingDay", "meetingMonth", "meetingYear", "meetingTime", "meetingPlace", "agendaText", null, "signName"],
  CapitalReduction: ["company", "dateDay", "dateMonth", "dateYear", "subjectMeetingNo1", "subjectMeetingNo2", "company", "company", "bodyMeetingNo1", "bodyMeetingNo2", "meetingDay", "meetingMonth", "meetingYear", "meetingTime", "meetingPlace", null, "signName"],
  CompanyRegulations: ["company", "dateDay", "dateMonth", "dateYear", "meetingNo", "company", "company", "bodyMeetingNo1", "bodyMeetingNo2", "meetingDay", "meetingMonth", "meetingYear", "meetingTime", "meetingPlace", "agendaText", null, "signName"],
  Notice2Form: ["company", "meetingNo", "company", "agenda3AtDate", "company", "meetingPlace", null, "issueDateText", null, "signName"],
  NoticeofCapitalReduction: ["company", "company", "agenda3AtDate", "oldCapital", "oldShares", "newCapital", "newShares", "meetingPlace", "issueDateText", null, "signName"],
  NoticeTransformPartnershipToCompany_TH: ["company", "issueDateText", "company", "newCompany", "meetingPlace", "agenda3AtDate", "signName"],
  Dissolution: ["company", "registrationNo", "company", "meetingDay", "meetingMonth", "meetingYear", "meetingPlace", "objectionDays", "issueDateText", "signName"],
  Distribute: ["company", "dateDay", "dateMonth", "dateYear", "company", "subjectMeetingNo1", "subjectMeetingNo2", "meetingDay", "meetingMonth", "meetingYear", "meetingTime", "meetingPlace", "dividendRate", "bookClosingDay", "bookClosingMonth", "bookClosingYear", "meetingTime", "paymentDay", "paymentMonth", "paymentYear", null, "signName"],
  InvitationEGM_EN: ["company", "issueDateEn", "subjectMeetingNo1", "subjectMeetingNo2", "bodyMeetingNo1", "bodyMeetingNo2", "meetingDateEn", "meetingTime", "meetingPlace", "agenda1No1", "agenda1No2", "meetingDateEn"],
  InvitationEGM2_EN: ["company", "subjectMeetingNo1", "subjectMeetingNo2", "company", "meetingDateEn", "meetingPlace", "signName"],
  InvitationEGM3_EN: ["company", "issueDateEn", "subjectMeetingNo1", "subjectMeetingNo2", "bodyMeetingNo1", "bodyMeetingNo2", "meetingDateEn", "meetingTime", "meetingPlace", "agenda1No1", "agenda1No2", "meetingDateEn", "agenda1No1", "agenda1No2", "signName"],
  MeetingInvitationEN: ["issueDateEn", "meetingNo", "company", "meetingNo", "meetingDateEn", "meetingTime", "meetingPlace", "meetingNo", null, "signName", "signPosition"],
};

const waitForNextPaint = () =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

const getCapturedControlText = (control) => {
  if (control.tagName === "SELECT") {
    return control.options[control.selectedIndex]?.text || control.value || "\u00a0";
  }

  return control.value || control.getAttribute("placeholder") || "\u00a0";
};

const getCaptureWidthBuffer = (source, rect) => {
  if (source.tagName === "TEXTAREA") return 0;

  const className = String(source.className || "");
  if (/\b(inv-line-tiny|inv-line-short|inv-line-date|en-input|nd-input)\b/i.test(className)) {
    return 14;
  }

  if (rect.width <= 40) return 16;
  if (rect.width <= 70) return 14;
  if (rect.width <= 110) return 10;
  return 0;
};

const isAddressLikeCaptureField = (source) => {
  const className = String(source.className || "");
  if (!/\binv-line-long\b/i.test(className)) return false;

  const contextText = String(source.parentElement?.textContent || "");
  return /(สำนักงาน|สถานที่|ที่อยู่|เลขที่|office|address|place)/i.test(contextText);
};

const copyCaptureStyles = (source, target, win) => {
  const style = win.getComputedStyle(source);
  const rect = source.getBoundingClientRect();
  const widthBuffer = getCaptureWidthBuffer(source, rect);
  const useNaturalTextWidth = isAddressLikeCaptureField(source);
  [
    "width",
    "minWidth",
    "maxWidth",
    "margin",
    "boxSizing",
    "fontFamily",
    "fontSize",
    "fontWeight",
    "fontStyle",
    "letterSpacing",
    "color",
    "verticalAlign",
    "whiteSpace",
    "background",
    "textDecoration",
  ].forEach((property) => {
    target.style[property] = style[property];
  });

  target.style.display = "inline-block";
  target.style.textAlign = style.textAlign;
  target.style.border = "0";
  target.style.borderBottom = "0";
  target.style.outline = "0";
  target.style.boxShadow = "none";
  target.style.height = "auto";
  target.style.minHeight = "0";
  target.style.lineHeight = "1";
  target.style.paddingTop = "0";
  target.style.paddingBottom = "0";
  target.style.paddingLeft = style.paddingLeft;
  target.style.paddingRight = style.paddingRight;
  target.style.overflow = "visible";

  if (useNaturalTextWidth) {
    target.style.width = "auto";
    target.style.minWidth = "0";
    target.style.maxWidth = "none";
    target.style.paddingLeft = "0";
    target.style.paddingRight = "0";
  } else if (Number.isFinite(rect.width) && rect.width > 0) {
    target.style.width = `${rect.width + widthBuffer}px`;
    target.style.minWidth = `${rect.width + widthBuffer}px`;
  }

  target.style.whiteSpace = source.tagName === "TEXTAREA" ? "pre-wrap" : "pre";

  if (process.env.NODE_ENV !== "production") {
    console.debug("mfw capture control rect", {
      tagName: source.tagName,
      className: source.className,
      width: rect.width,
      height: rect.height,
    });
  }
};

// a value longer than its box (an address) becomes text that wraps with the sentence instead of a one-line block that
// runs past the paper edge and gets cut off (owner 28-10-2569: "บางข้อความแสดงไม่ครบ")
const flowIfOverflowing = (control, span) => {
  if (control.tagName === "SELECT" || !(control.scrollWidth > control.clientWidth + 1)) return;
  span.style.display = "inline";
  span.style.width = "auto";
  span.style.minWidth = "0";
  span.style.maxWidth = "none";
  span.style.whiteSpace = "pre-wrap";
  span.style.margin = "0 4px";
};

// fields still empty on the paper (inputs, or the frozen copies in "แก้ไขข้อความ / วาระ"); optional ones are skipped
const findEmptyFields = (root, freeEditing) => {
  if (!root) return [];
  if (freeEditing) {
    return Array.from(root.querySelectorAll('.mfw-capture-text[data-empty="1"]')).filter((s) => {
      const text = s.textContent.replace(/ /g, " ").trim();
      return !text || text === (s.dataset.placeholder || "").trim();
    });
  }
  return Array.from(root.querySelectorAll('input:not([type="hidden"]), textarea, select')).filter(
    (c) => !c.disabled && !c.dataset.optional && !String(c.value || "").trim()
  );
};

const replaceControlsForCapture = (clonedRoot, clonedDocument) => {
  const win = clonedDocument.defaultView;
  if (!win) return;

  clonedRoot
    .querySelectorAll('input:not([type="hidden"]), textarea, select')
    .forEach((control) => {
      const span = clonedDocument.createElement("span");
      span.className = control.className;
      span.classList.add("mfw-capture-text");
      span.textContent = getCapturedControlText(control);
      copyCaptureStyles(control, span, win);
      flowIfOverflowing(control, span);
      control.replaceWith(span);
    });
};

function FormWrapper({ title, FormComponent, formKey }) {
  const [issueDate, setIssueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [status, setStatus] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const [isPrintMode, setIsPrintMode] = useState(false);
  const printMode = useMemo(() => ({ isCapturing, isPrintMode }), [isCapturing, isPrintMode]);
  const [initialData, setInitialData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  // the next notice of the same customer starts with the contact from the basket
  const [contact, setContact] = useState(() => ({ contactName: "", phone: "", companyName: "", requestedDate: "", ...readBasket().contact }));
  const [submitted, setSubmitted] = useState(null);
  const location = useLocation();
  const { siteContent } = useSiteContent();
  // staff keep the direct "save to daily edition" flow; everyone else submits for review
  const [isAdmin, setIsAdmin] = useState(isAdminLoggedIn);
  // an admin can try the customer flow (send → payment) without logging out (owner 28-10-2569: testing while logged
  // in as admin silently saved straight into the newspaper)
  const [asCustomer, setAsCustomer] = useState(false);
  const adminMode = isAdmin && !asCustomer;
  // the session hint can be stale (cookie expired / logged out elsewhere) → ask the server
  useEffect(() => {
    let active = true;
    checkAdminSession().then((ok) => { if (active) setIsAdmin(ok); });
    return () => { active = false; };
  }, []);

  const captureRef = useRef(null);

  // ── Free-edit mode: the customer edits wording / agendas directly on a copy of the paper ──
  const [freeEdit, setFreeEdit] = useState(false);
  const [paperHtml, setPaperHtml] = useState("");
  const freeRef = useRef(null);
  const historyRef = useRef([]);
  const snapshotValuesRef = useRef([]);

  // the element that is turned into the announcement image
  const captureTarget = () => (freeEdit ? freeRef.current?.firstElementChild : captureRef.current);

  const startFreeEdit = () => {
    const el = captureRef.current;
    if (!el) return;
    const selector = 'input:not([type="hidden"]), textarea, select';
    snapshotValuesRef.current = Array.from(el.querySelectorAll("input, textarea, select")).map((i) => i.value);
    // copy the paper and turn every field into text that looks exactly like the field did
    const clone = el.cloneNode(true);
    const live = el.querySelectorAll(selector);
    clone.querySelectorAll(selector).forEach((copy, i) => {
      const source = live[i];
      const span = document.createElement("span");
      span.className = source.className;
      span.classList.add("mfw-capture-text");
      span.textContent = getCapturedControlText(source);
      copyCaptureStyles(source, span, window);
      span.style.width = "auto"; // typed text may get longer than the field was
      flowIfOverflowing(source, span);
      // remember fields left empty (their placeholder shows instead) — sending is blocked while one is still there
      if (!String(source.value || "").trim() && !source.dataset?.optional) {
        span.dataset.empty = "1";
        span.dataset.placeholder = source.getAttribute("placeholder") || "";
      }
      copy.replaceWith(span);
    });
    historyRef.current = [];
    setPaperHtml(clone.outerHTML);
    setFreeEdit(true);
    setStatus("");
  };

  // fill the editable copy once, when editing starts
  useLayoutEffect(() => {
    if (freeEdit && freeRef.current && paperHtml) freeRef.current.innerHTML = paperHtml;
  }, [freeEdit, paperHtml]);

  const cancelFreeEdit = () => {
    if (!window.confirm("ยกเลิกข้อความที่แก้บนกระดาษ แล้วกลับไปกรอกแบบฟอร์ม?")) return;
    setFreeEdit(false);
    setPaperHtml("");
    setStatus("");
  };

  const editOp = (fn, failText) => {
    const root = freeRef.current;
    if (!root) return;
    historyRef.current.push(root.innerHTML);
    if (fn(root) === false) {
      historyRef.current.pop();
      setStatus(failText);
    } else {
      setStatus("");
    }
  };

  const undoEdit = () => {
    const root = freeRef.current;
    const prev = historyRef.current.pop();
    if (root && prev !== undefined) root.innerHTML = prev;
  };

  // keep the caret/selection on the paper when a toolbar button is pressed
  const keepSelection = (e) => e.preventDefault();

  const getRouteParams = () => {
    if (window.location.search) {
      return new URLSearchParams(window.location.search);
    }

    const hash = window.location.hash || "";
    const queryStart = hash.indexOf("?");
    return queryStart >= 0
      ? new URLSearchParams(hash.slice(queryStart + 1))
      : new URLSearchParams();
  };

  const getTemplateInputValues = (formData) => {
    const route = window.location.hash || window.location.pathname || "";
    const page = route.replace(/^#?\/?/, "").split(/[?/]/)[0];
    if (IMPORT_FIELD_ORDER[page]) return IMPORT_FIELD_ORDER[page].map((k) => (k ? formData[k] : undefined));
    const key = String(formKey || "").toLowerCase();

    if (route.includes("NoticeofCapitalReduction") || key.includes("notice-of-capital-reduction")) {
      return [
        formData.company,
        formData.company,
        formData.agenda3AtDate || formData.meetingDate,
        formData.oldCapital,
        formData.oldShares,
        formData.newCapital,
        formData.newShares,
        formData.meetingPlace,
        formData.dateDay || formData.issueDate,
        formData.signName,
      ];
    }

    if (route.includes("/Dissolution?") || route.endsWith("/Dissolution") || key === "dissolution") {
      return [
        formData.company,
        formData.registrationNo,
        formData.company,
        formData.meetingDay,
        formData.meetingMonth,
        formData.meetingYear,
        formData.meetingPlace,
        formData.objectionDays,
        formData.dateDay || formData.issueDate,
      ];
    }

    if (key.includes("distribute")) {
      return [
        formData.company,
        formData.dateDay,
        formData.dateMonth,
        formData.dateYear,
        formData.announceTo,
        formData.subjectMeetingNo1,
        formData.subjectMeetingNo2,
        formData.meetingDay,
        formData.meetingMonth,
        formData.meetingYear,
        formData.meetingTime,
        formData.meetingPlace,
        formData.dividendRate,
        formData.bookClosingDay,
        formData.bookClosingMonth,
        formData.bookClosingYear,
        formData.meetingTime,
        formData.paymentDay,
        formData.paymentMonth,
        formData.paymentYear,
        formData.signName,
      ];
    }

    if (key.includes("noticetransformpartnershiptocompany")) {
      return [
        formData.company,
        formData.dateDay || formData.issueDate,
        formData.company,
        formData.newCompany,
        formData.meetingPlace,
        formData.agenda3AtDate || formData.meetingDate,
        formData.signName,
      ];
    }

    return [
      formData.company,
      formData.dateDay,
      formData.dateMonth,
      formData.dateYear,
      formData.subjectMeetingNo1,
      formData.subjectMeetingNo2,
      formData.bodyMeetingNo1,
      formData.bodyMeetingNo2,
      formData.meetingDay,
      formData.meetingMonth,
      formData.meetingYear,
      formData.meetingTime,
      formData.meetingPlace,
      formData.meetingPlace2,
      formData.agenda1No1,
      formData.agenda1No2,
      formData.agenda3AtDate,
      formData.signName,
      formData.signPosition,
    ];
  };

  const applyInitialDataToInputs = (formData) => {
    const root = captureRef.current;
    if (!root || !formData) return;

    const valuesInTemplateOrder = getTemplateInputValues(formData);

    // The fields are React-controlled (PrintField keeps the text in state). Setting input.value directly lets React's
    // value tracker see no change, so onChange never ran: the value vanished on the next render and the captured
    // image came out blank. The native setter + an "input" event goes through React's onChange like typing does.
    const setNativeValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
    const inputs = Array.from(root.querySelectorAll("input"));
    inputs.forEach((input, index) => {
      const value = valuesInTemplateOrder[index];
      if (value === undefined || value === null || value === "") return;
      setNativeValue.call(input, String(value));
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  };

  useEffect(() => {
    const params = getRouteParams();
    const previewId = params.get("previewId");
    const importId = params.get("importId");
    if (!previewId && !importId) return;

    if (previewId) {
      try {
        const raw = sessionStorage.getItem(`excelPreview:${previewId}`);
        if (!raw) {
          setStatus("ไม่พบข้อมูล preview item");
          return;
        }

        const item = JSON.parse(raw);
        const previewFormData = normalizeImportedFormData(item.formData || {});
        setInitialData(previewFormData);
        if (previewFormData.issueDate) setIssueDate(previewFormData.issueDate);
        setTimeout(() => applyInitialDataToInputs(previewFormData), 0);
      } catch (err) {
        console.error(err);
        setStatus("โหลดข้อมูล preview ไม่สำเร็จ");
      }
      return;
    }

    let cancelled = false;

    async function loadImportedData() {
      try {
        setImportLoading(true);
        const res = await fetch(`${API_BASE}/api/excel/imported/${importId}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(data.message || "โหลดข้อมูล import ไม่สำเร็จ");
        }

        if (cancelled) return;

        const importedFormData = normalizeImportedFormData(data.item?.formData || {});
        setInitialData(importedFormData);
        if (importedFormData.issueDate) setIssueDate(importedFormData.issueDate);

        applyInitialDataToInputs(importedFormData);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setStatus(err.message || "โหลดข้อมูล import ไม่สำเร็จ");
        }
      } finally {
        if (!cancelled) setImportLoading(false);
      }
    }

    loadImportedData();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!initialData) return;
    const timer = setTimeout(() => applyInitialDataToInputs(initialData), 0);
    return () => clearTimeout(timer);
  }, [initialData]);

  // every field on the paper must be filled before it is saved / sent (owner 28-10-2569: the form could be sent with
  // empty essential fields). Empty ones are marked, the first one is scrolled to; a line that is not needed can be
  // removed with "แก้ไขข้อความ / วาระ".
  const blockIfEmpty = () => {
    const root = freeEdit ? freeRef.current : captureRef.current;
    root?.querySelectorAll(".mfw-field-missing").forEach((n) => n.classList.remove("mfw-field-missing"));
    const empty = findEmptyFields(root, freeEdit);
    if (!empty.length) return false;
    empty.forEach((n) => n.classList.add("mfw-field-missing"));
    empty[0].scrollIntoView({ behavior: "smooth", block: "center" });
    if (!freeEdit) empty[0].focus({ preventScroll: true });
    setStatus(
      `ยังกรอกไม่ครบ ${empty.length} ช่อง (ช่องที่มีกรอบสีแดง) · กรุณากรอกให้ครบก่อนบันทึก`
      + " — ถ้าช่องไหนไม่ใช้ ให้กด \"แก้ไขข้อความ / วาระ\" แล้วลบบรรทัดนั้นออก"
    );
    return true;
  };

  // the red mark goes away as soon as the field gets a value
  useEffect(() => {
    const root = captureRef.current;
    if (!root) return undefined;
    const clear = (e) => {
      if (e.target.classList?.contains("mfw-field-missing") && String(e.target.value || "").trim()) {
        e.target.classList.remove("mfw-field-missing");
      }
    };
    root.addEventListener("input", clear);
    return () => root.removeEventListener("input", clear);
  });

  const handleSave = async () => {
    if (!issueDate) {
      alert("กรุณาเลือก IssueDate (วันที่หนังสือพิมพ์ออก)");
      return;
    }
    if (blockIfEmpty()) return;

    const el = captureTarget();
    if (!el) {
      alert("ไม่พบ element สำหรับแคปเจอร์");
      return;
    }

    setSaving(true);
    setStatus("กำลังบันทึกใบประกาศ...");

    try {
      document.activeElement?.blur?.();
      setIsCapturing(true);
      await waitForNextPaint();
      if (document.fonts?.ready) await document.fonts.ready;

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        // always lay the paper out as a desktop A4 page — on a phone / narrow window the paper used to
        // reflow to the screen width, so the saved image came out squeezed (e.g. 558px wide instead of 210mm)
        windowWidth: CAPTURE_WINDOW_WIDTH,
        onclone: (clonedDocument, clonedElement) => {
          clonedElement.style.width = "210mm";
          clonedElement.style.maxWidth = "none";
          clonedElement.style.minWidth = "210mm";
          clonedElement.style.border = "0";
          clonedElement.style.outline = "0";
          clonedElement.style.boxShadow = "none";
          replaceControlsForCapture(clonedElement, clonedDocument);
        },
      });

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) {
        setStatus("ไม่สามารถสร้างรูปได้");
        alert("ไม่สามารถสร้างรูปได้");
        return;
      }

      const data = await uploadAnnouncementImage({
        fileOrBlob: blob,
        filename: "meeting-form.png",
        issueDate,
      });

      alert(`บันทึกสำเร็จ! ID = ${data.id}`);
      setStatus("บันทึกสำเร็จ");
    } catch (err) {
      console.error(err);
      alert(`เกิดข้อผิดพลาด: ${err?.message || "Unknown error"}`);
      setStatus("เกิดข้อผิดพลาด");
    } finally {
      setIsCapturing(false);
      setSaving(false);
    }
  };

  const handlePrintPdf = async () => {
    const el = captureTarget();
    if (!el) {
      alert("ไม่พบ element สำหรับแคปเจอร์");
      return;
    }

    setPrinting(true);
    setStatus("กำลังเตรียม Print / PDF...");

    let fallbackTimer;
    const cleanup = () => {
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      window.removeEventListener("afterprint", cleanup);
      setIsPrintMode(false);
      setPrinting(false);
      setStatus("");
    };

    try {
      document.activeElement?.blur?.();
      setIsPrintMode(true);
      await waitForNextPaint();
      if (document.fonts?.ready) await document.fonts.ready;
      await waitForNextPaint();

      window.addEventListener("afterprint", cleanup, { once: true });
      fallbackTimer = window.setTimeout(cleanup, 5000);
      window.print();
    } catch (err) {
      console.error(err);
      alert(`เกิดข้อผิดพลาดตอนสร้าง Print / PDF: ${err?.message || "Unknown error"}`);
      setStatus("เกิดข้อผิดพลาดตอนสร้าง Print / PDF");
      cleanup();
    }
  };

  // Customer flow (new newspaper system): capture the paper as an image and send it as a
  // "template" submission. It waits for payment + editor approval before it is published.
  const handleSubmitToEditor = async (event) => {
    event.preventDefault();
    if (blockIfEmpty()) return;
    const el = captureTarget();
    if (!el) return;

    // read the typed values before capture mode swaps the inputs for plain text
    // (in free-edit mode the fields were frozen into text when editing started)
    const fieldValues = freeEdit
      ? snapshotValuesRef.current
      : Array.from(el.querySelectorAll("input, textarea, select")).map((i) => i.value);
    const editedText = freeEdit ? el.innerText : null;

    setSaving(true);
    setStatus("กำลังเตรียมภาพประกาศ...");
    try {
      document.activeElement?.blur?.();
      setIsCapturing(true);
      await waitForNextPaint();
      if (document.fonts?.ready) await document.fonts.ready;

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        // always lay the paper out as a desktop A4 page — on a phone / narrow window the paper used to
        // reflow to the screen width, so the saved image came out squeezed (e.g. 558px wide instead of 210mm)
        windowWidth: CAPTURE_WINDOW_WIDTH,
        onclone: (clonedDocument, clonedElement) => {
          clonedElement.style.width = "210mm";
          clonedElement.style.maxWidth = "none";
          clonedElement.style.minWidth = "210mm";
          clonedElement.style.border = "0";
          clonedElement.style.outline = "0";
          clonedElement.style.boxShadow = "none";
          replaceControlsForCapture(clonedElement, clonedDocument);
        },
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("ไม่สามารถสร้างภาพประกาศได้");

      const formType = findFormType(location.pathname);
      const fd = new FormData();
      fd.append("method", "template");
      fd.append("title", formType?.name || title || "ประกาศ");
      fd.append("category", formType?.category || "ประกาศบริษัท");
      fd.append("formType", formType?.path || formKey || location.pathname);
      fd.append("formData", JSON.stringify(editedText ? { fields: fieldValues, editedText } : fieldValues));
      Object.entries(contact).forEach(([k, v]) => fd.append(k, v));
      fd.append("files", blob, "announcement.png");

      setStatus("กำลังส่งประกาศ...");
      // contact + title go into the basket of notices to pay for together
      setSubmitted({ ...(await submitAnnouncement(fd)), ...contact, title: formType?.name || title || "ประกาศ" });
      setStatus("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setStatus(err?.message || "ส่งประกาศไม่สำเร็จ");
    } finally {
      setIsCapturing(false);
      setSaving(false);
    }
  };

  const setContactField = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  return (
    <main id="main" className="mfw-page container">
      <Link className="breadcrumb" to="/newspaper/submit?method=template">← เลือกประเภทประกาศ</Link>
      <p className="eyebrow">{adminMode ? "ADMIN \u00b7 ลงประกาศ" : "วิธีที่ 1 \u00b7 ลงประกาศด้วยตัวเอง"}</p>
      <h1 className="mfw-title">{findFormType(location.pathname)?.name || title}</h1>
      {isAdmin && !submitted && (
        <div className={`mfw-admin-note${adminMode ? "" : " customer"}`} role="note">
          {adminMode ? (
            <span>
              คุณเข้าสู่ระบบแอดมินอยู่ · &quot;บันทึกเป็นใบประกาศ&quot; จะลงประกาศในหนังสือพิมพ์ทันที <strong>ไม่ผ่านการชำระเงิน</strong>
            </span>
          ) : (
            <span>โหมดทดสอบแบบลูกค้า · ส่งแล้วจะไปขั้นตอนชำระเงินเหมือนลูกค้าจริง (รายการจากเครื่องทดสอบจะติดป้าย &quot;ทดสอบ&quot;)</span>
          )}
          <button type="button" className="text-link" onClick={() => setAsCustomer((v) => !v)}>
            {adminMode ? "ทดสอบแบบลูกค้า →" : "← กลับโหมดแอดมิน"}
          </button>
        </div>
      )}

      {submitted ? (
        <Success c={siteContent.submitPage || {}} result={submitted} />
      ) : adminMode ? (
        <div className="mfw-controls">
          <label>
            {"IssueDate (วันที่หนังสือพิมพ์ออก): "}
            <DateInput
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </label>

          <button className="mfw-btn-save" disabled={saving || printing} onClick={handleSave}>
            {saving ? "กำลังบันทึก..." : "บันทึกเป็นใบประกาศ"}
          </button>

          <button className="mfw-btn-save" type="button" disabled={saving || printing} onClick={handlePrintPdf}>
            {printing ? "Creating..." : "Print / PDF"}
          </button>
        </div>
      ) : (
        <form className="mfw-controls mfw-submit site-form" onSubmit={handleSubmitToEditor}>
          <label>
            ชื่อผู้ติดต่อ <span className="required">*</span>
            <input required value={contact.contactName} onChange={setContactField("contactName")} autoComplete="name" />
          </label>
          <label>
            เบอร์โทร <span className="required">*</span>
            <input required type="tel" value={contact.phone} onChange={setContactField("phone")} autoComplete="tel" />
          </label>
          <label>
            บริษัท / กิจการ
            <input value={contact.companyName} onChange={setContactField("companyName")} autoComplete="organization" />
          </label>
          <label>
            วันที่ต้องการลงประกาศ
            <DateInput value={contact.requestedDate} onChange={setContactField("requestedDate")} />
          </label>
          <button className="button dark" type="submit" disabled={saving || printing}>
            {saving ? "กำลังส่ง..." : "ส่งประกาศ"}{" "}
            <span>↗</span>
          </button>
          <button className="button outline" type="button" disabled={saving || printing} onClick={handlePrintPdf}>
            {printing ? "กำลังเตรียม..." : "ดูตัวอย่าง / พิมพ์"}
          </button>
        </form>
      )}

      {!submitted && (
        <div className="mfw-edit-bar">
          {!freeEdit ? (
            <>
              <button type="button" className="button outline" onClick={startFreeEdit} disabled={saving || printing}>
                แก้ไขข้อความ / วาระ{" "}
                <span>✎</span>
              </button>
              <span className="mfw-edit-hint">กรอกช่องต่าง ๆ ก่อน แล้วกดปุ่มนี้เพื่อแก้ถ้อยคำ เพิ่ม ลด หรือแก้ไขวาระบนกระดาษ</span>
            </>
          ) : (
            <>
              <span className="mfw-edit-hint strong">โหมดแก้ไข: คลิกข้อความบนกระดาษเพื่อแก้ไขได้ทุกจุด</span>
              <button type="button" className="button dark" onMouseDown={keepSelection}
                onClick={() => editOp(addAgenda, "แบบฟอร์มนี้ไม่มีรายการวาระ — คลิกที่ข้อความแล้วพิมพ์เพิ่มได้โดยตรง")}>
                + เพิ่มวาระ
              </button>
              <button type="button" className="button outline" onMouseDown={keepSelection}
                onClick={() => editOp(removeBlockAtSelection, "คลิกที่บรรทัดหรือวาระที่ต้องการลบก่อน แล้วกดอีกครั้ง")}>
                ลบบรรทัดที่เลือก
              </button>
              <button type="button" className="button outline" onMouseDown={keepSelection}
                onClick={() => editOp((root) => { renumberAgendas(root); })}>
                เรียงเลขวาระใหม่
              </button>
              <button type="button" className="text-link" onMouseDown={keepSelection} onClick={undoEdit}>
                ย้อนกลับ
              </button>
              <button type="button" className="text-link mfw-cancel-edit" onClick={cancelFreeEdit}>
                ยกเลิกการแก้ไข
              </button>
            </>
          )}
        </div>
      )}

      {status && <div className="mfw-status">{status}</div>}
      {importLoading && <div className="mfw-status">กำลังโหลดข้อมูล import...</div>}

      <div className="mfw-print-scope">
        <div
          className={`mfw-preview-wrapper ${
            isCapturing || isPrintMode ? "mfw-preview-wrapper-clean" : ""
          }`}
        >
          {/* the form stays mounted while editing, so "ยกเลิกการแก้ไข" keeps everything typed */}
          <div className="mfw-form-holder" style={freeEdit ? { display: "none" } : undefined}>
            <PrintModeContext.Provider value={printMode}>
              <FormComponent
                ref={captureRef}
                isCapturing={isCapturing}
                isPrintMode={isPrintMode}
                initialData={initialData}
              />
            </PrintModeContext.Provider>
          </div>
          {freeEdit && (
            // React renders this box empty and never manages its children: the paper copy is put in
            // once (effect below), so re-renders cannot wipe what the customer typed
            <div
              ref={freeRef}
              className="mfw-free-paper"
              contentEditable
              suppressContentEditableWarning
              spellCheck={false}
            />
          )}
        </div>
      </div>
    </main>
  );
}

export default FormWrapper;
